import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  approveBillingRequest,
  fetchBillingRequests,
  rejectBillingRequest,
} from "@/lib/platform-api";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function formatMoney(usd) {
  if (usd == null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(usd);
}

function requestSummary(r) {
  if (r.requestType === "upgrade") {
    return `Upgrade to ${r.requestedPlan || "—"}`;
  }
  if (r.requestType === "gov_access") {
    return "Government integration (Enterprise)";
  }
  if (r.requestType === "whitelabel_support") {
    return "Whitelabel / email support request";
  }
  return `Recharge ${formatMoney(r.rechargeAmountUsd || 0)}`;
}

export default function PlatformBilling() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [busyId, setBusyId] = useState("");
  const [reviewNotes, setReviewNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBillingRequests(statusFilter);
      setRequests(data.requests || []);
    } catch (err) {
      toast.error("Could not load billing queue", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const notesFor = (id) => reviewNotes[id] || "";

  const onApprove = async (id) => {
    setBusyId(id);
    try {
      await approveBillingRequest(id, notesFor(id));
      toast.success("Request approved");
      setReviewNotes((n) => ({ ...n, [id]: "" }));
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId("");
    }
  };

  const onReject = async (id) => {
    setBusyId(id);
    try {
      await rejectBillingRequest(id, notesFor(id));
      toast.success("Request rejected");
      setReviewNotes((n) => ({ ...n, [id]: "" }));
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto">
      <div className="text-overline">Platform</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Billing queue</h1>
      <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-2xl leading-relaxed">
        Approve or reject workspace upgrade and credit recharge requests. Approving an upgrade updates the org
        subscription; approving recharge creates a pending invoice.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <label className="text-xs text-[var(--text-tertiary)]">Show</label>
        <select
          className="input-dark text-sm max-w-[200px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="submitted">Pending (submitted)</option>
          <option value="in_review">In review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <button type="button" className="btn-ghost text-sm" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-[var(--text-secondary)]">Loading requests…</p>
      ) : requests.length === 0 ? (
        <p className="mt-10 text-sm text-[var(--text-tertiary)]">No requests in this filter.</p>
      ) : (
        <div className="mt-8 space-y-4">
          {requests.map((r) => {
            const pending = r.status === "submitted" || r.status === "in_review";
            const busy = busyId === r.id;
            return (
              <div key={r.id} className="surface p-5">
                <div className="flex flex-wrap justify-between gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)]">
                      {r.requestType}
                    </div>
                    <div className="font-heading text-xl mt-1">{r.orgName || r.tenantId}</div>
                    <div className="text-sm text-[var(--text-secondary)] mt-1">{requestSummary(r)}</div>
                    <div className="text-xs font-mono text-[var(--text-tertiary)] mt-2">
                      Tenant <span className="text-[var(--text-secondary)]">{r.tenantId}</span>
                      {r.currentPlan ? (
                        <>
                          {" "}
                          · current plan <span className="capitalize">{r.currentPlan}</span>
                        </>
                      ) : null}
                    </div>
                    {r.notes ? (
                      <p className="mt-3 text-sm text-[var(--text-secondary)] border-l border-[var(--border-default)] pl-3">
                        {r.notes}
                      </p>
                    ) : null}
                  </div>
                  <div className="text-right text-xs font-mono">
                    <span className="capitalize text-[var(--text-primary)]">{r.status}</span>
                    <div className="mt-1 text-[var(--text-tertiary)]">{formatDate(r.createdAt)}</div>
                    <div className="mt-1 text-[var(--text-tertiary)]">by {r.createdBy || "—"}</div>
                  </div>
                </div>

                {pending ? (
                  <div className="mt-4 pt-4 border-t border-[var(--border-default)]">
                    <label className="text-xs text-[var(--text-tertiary)]">Review notes (optional)</label>
                    <textarea
                      className="input-dark w-full mt-1 min-h-[64px] text-sm"
                      placeholder="Internal note or message to customer…"
                      value={notesFor(r.id)}
                      onChange={(e) => setReviewNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn-primary text-sm"
                        disabled={busy}
                        onClick={() => onApprove(r.id)}
                      >
                        {busy ? "Working…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        disabled={busy}
                        onClick={() => onReject(r.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ) : r.reviewNotes || r.reviewedBy ? (
                  <div className="mt-3 text-xs text-[var(--text-tertiary)]">
                    {r.reviewedBy ? <span>Reviewed by {r.reviewedBy}</span> : null}
                    {r.reviewNotes ? <p className="mt-1">{r.reviewNotes}</p> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
